import React, { memo, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import { keyframes, styled } from '@mui/material/styles'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'

import waveBot from '../../../images/bottomwave.png'
import CropRight from '../../../images/Crop_right.svg'
import navyBkg from '../../../images/navy-bkg.jpg'
import waveTop from '../../../images/topwave-flipped.png'
import { useAuthContext } from '../../../utils/context/AuthContext'
import { LoadableWistiaVideo } from '../../Loaders/loadableComponents'
import ErrorBoundary from '../../ErrorBoundary'

// Define color variables (ensure these are defined consistently across your theme)
const BLUE_BASE = '#1976d2'
const LIGHT_BG = '#f0f4f8'
const TEXT_COLOR = '#ffffff'
const ACCENT_COLOR = '#D8B45A'

// Define a subtle animation for visual enhancement
const fadeIn = keyframes`
  from {
    opacity: 0.95;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const topLeftStyle = { top: '-10px', left: '-10px' }
const bottomRightStyle = {
  bottom: '-10px',
  right: '-10px',
  transform: 'scaleX(-1)',
}

// Styled Components
const BlueContainer = styled(Box)(({ theme }) => ({
  backgroundImage: `url(${waveTop}), url(${waveBot}), url(${navyBkg})`,
  backgroundRepeat: 'no-repeat, no-repeat, no-repeat',
  backgroundPosition: 'top center, bottom center, center',
  backgroundSize: '100% 100px, 100% 100px, cover',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '100px 5%',
  color: TEXT_COLOR,
  fontSize: '18px',
  lineHeight: '32px',
  fontWeight: 300,
  flexDirection: 'column',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  borderRadius: '12px',
  animation: `${fadeIn} 5s ease-in-out infinite alternate`,
  [theme.breakpoints.down('sm')]: {
    backgroundSize: '100% 60px, 100% 60px, cover',
    padding: '50px 2%',
  },
}))

const GridContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(4),
  width: '100%',
  maxWidth: '1200px',
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
}))

const ContentSection = styled(Box)(({ theme, order }) => ({
  order,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  padding: theme.spacing(4),
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  animation: `${fadeIn} 10s ease-in-out infinite`,
  [theme.breakpoints.up('sm')]: {
    alignItems: 'flex-start',
  },
}))

const VideoSection = styled(Box)(({ theme, order }) => ({
  order,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(0, 4),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0, 2),
    marginTop: theme.spacing(2.5),
  },
}))

const QuoteBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  maxWidth: '700px',
  margin: 'auto',
  padding: theme.spacing(3),
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  [theme.breakpoints.down('xs')]: {
    padding: theme.spacing(2),
  },
}))

const TextBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  textAlign: 'center',
  maxWidth: '900px',
  padding: theme.spacing(0, 4),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('xs')]: {
    padding: theme.spacing(1, 2, 0, 2),
  },
}))

const QuoteText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Poppins, sans-serif',
  fontStyle: 'italic',
  fontWeight: 300,
  fontSize: '24px',
  lineHeight: '1.8',
  color: '#ffffff',
  padding: theme.spacing(1, 5),
  [theme.breakpoints.down('xs')]: {
    fontSize: '16px',
    lineHeight: '1.5',
    padding: theme.spacing(0.5, 2.5),
  },
}))

const AuthorName = styled(Typography)(({ theme }) => ({
  fontSize: '20px',
  marginBottom: theme.spacing(1.25),
  color: ACCENT_COLOR,
}))

const AuthorTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Poppins, sans-serif',
  fontSize: '16px',
  lineHeight: '1.2',
  color: '#D8B45A',
}))

const Headshot = styled(GatsbyImage)(({ theme }) => ({
  borderRadius: '50%',
  height: '120px',
  width: '120px',
  margin: theme.spacing(2, 0),
  border: `3px solid ${ACCENT_COLOR}`,
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  [theme.breakpoints.down('sm')]: {
    height: '80px',
    width: '80px',
    margin: theme.spacing(1, 0),
  },
}))

const VideoContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
}))

const DecorativeImage = styled('img')(({ theme }) => ({
  position: 'absolute',
  width: '30px',
  height: '30px',
  [theme.breakpoints.down('xs')]: {
    width: '20px',
    height: '20px',
  },
}))

// Helper to determine order based on video_position and mobile view
const determineOrder = (videoPosition, isMobile) => {
  if (isMobile) {
    return {
      content: 1,
      video: 2,
    }
  }
  return videoPosition === 'right'
    ? { content: 1, video: 2 }
    : { content: 2, video: 1 }
}

const GenerousQuote = ({ data }) => {
  const theme = useTheme()
  const auth = useAuthContext()
  const { userHasLoaded: hasUserLoaded } = auth?.state ?? {}
  const author = data.author[0]

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { content: contentOrder, video: videoOrder } = useMemo(
    () => determineOrder(data.video_position, isMobile),
    [data.video_position, isMobile]
  )

  return (
    <BlueContainer role="region" aria-labelledby="generous-quote-heading">
      <GridContainer>
        <ContentSection order={contentOrder}>
          <TextBox>
            {author?.photo?.localAsset && (
              <Headshot
                image={getImage(author.photo.localAsset)}
                alt={`${author.title}'s headshot`}
              />
            )}
            <AuthorName variant="h6" component="h3">
              <strong>{author?.title}</strong>
            </AuthorName>
            <AuthorTitle variant="subtitle1">{author?.job_title}</AuthorTitle>
          </TextBox>
          <QuoteBox aria-label="Quote">
            <DecorativeImage
              src={CropRight}
              alt=""
              aria-hidden="true"
              style={topLeftStyle}
            />
            <QuoteText component="blockquote"></QuoteText>
            <DecorativeImage
              src={CropRight}
              alt=""
              aria-hidden="true"
              style={bottomRightStyle}
            />
          </QuoteBox>
        </ContentSection>
        {data.wistia_video_id && hasUserLoaded && (
          <VideoSection order={videoOrder}>
            <VideoContainer>
              <ErrorBoundary>
                <LoadableWistiaVideo
                  id={data.wistia_video_id}
                  width="100%"
                  aria-label="Related video"
                />
              </ErrorBoundary>
            </VideoContainer>
          </VideoSection>
        )}
      </GridContainer>
    </BlueContainer>
  )
}

GenerousQuote.propTypes = {
  data: PropTypes.shape({
    author: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        job_title: PropTypes.string,
        photo: PropTypes.shape({
          localAsset: PropTypes.object,
        }),
      })
    ).isRequired,
    quote: PropTypes.string.isRequired,
    wistia_video_id: PropTypes.string,
    video_position: PropTypes.oneOf(['left', 'right']),
  }).isRequired,
}

export default memo(GenerousQuote)